import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "../i18n";
import { captureSentryException } from "../lib/sentry";

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    captureSentryException(error, { extra: { componentStack: info.componentStack } });
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
  }

  private handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          color: "#0a0a0a",
          background: "#ffffff",
        }}
      >
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.5rem", margin: "0 0 0.75rem" }}>
            {i18n.t("errorBoundary.title")}
          </h1>
          <p style={{ margin: "0 0 1.25rem", color: "#525252" }}>
            {i18n.t("errorBoundary.body")}
          </p>
          <button
            onClick={this.handleReload}
            style={{
              padding: "0.6rem 1.1rem",
              borderRadius: 8,
              border: "1px solid #0a0a0a",
              background: "#0a0a0a",
              color: "#ffffff",
              cursor: "pointer",
              fontSize: "0.95rem",
            }}
          >
            {i18n.t("errorBoundary.retry")}
          </button>
          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: "1.25rem",
                padding: "0.75rem",
                background: "#fafafa",
                border: "1px solid #e5e5e5",
                borderRadius: 6,
                textAlign: "left",
                fontSize: "0.75rem",
                overflow: "auto",
              }}
            >
              {this.state.error.stack ?? this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
